// Karams Festival theme system — single source of truth for the platform's look.
// A global theme (festivalArchive.theme) drives every accent via CSS variables.
// Each edition may override it (edition.useGlobalTheme === false + edition.theme).

export const DEFAULT_THEME = {
  primary: "#2e6bff", // electric blue accent
  bg: "#050505", // deep black background
  text: "#ffffff", // primary text
  textMuted: "#94a0b3", // secondary text
  intensity: 1, // accent glow intensity (0.4 – 1.6)
};

// Global appearance of the Festival modal windows (border + glow).
export const DEFAULT_MODAL = {
  colorMode: "edition", // "edition" = follow the edition accent, "custom" = fixed colour
  color: "#2e6bff", // used only when colorMode === "custom"
  width: 1, // border thickness in px (0 – 4)
  glow: 0, // outer glow intensity (0 – 1.5, 0 = none)
};

// Curated modern font stacks (streaming-platform vibe), incl. Inter.
export const FONT_OPTIONS = [
  { value: "'Inter', sans-serif", label: "Inter (recommandé)" },
  { value: "'Roboto', sans-serif", label: "Roboto" },
  { value: "'Outfit', sans-serif", label: "Outfit (actuel)" },
  { value: "'Poppins', sans-serif", label: "Poppins" },
  { value: "'Montserrat', sans-serif", label: "Montserrat" },
  { value: "'Manrope', sans-serif", label: "Manrope" },
  { value: "'Space Grotesk', sans-serif", label: "Space Grotesk" },
  { value: "'DM Sans', sans-serif", label: "DM Sans" },
  { value: "'Sora', sans-serif", label: "Sora" },
  { value: "'Cormorant Garamond', serif", label: "Cormorant Garamond (actuel)" },
  { value: "'Playfair Display', serif", label: "Playfair Display" },
];

export const WEIGHT_OPTIONS = [
  { value: 400, label: "Regular" },
  { value: 500, label: "Medium" },
  { value: 600, label: "SemiBold" },
  { value: 700, label: "Bold" },
];

// Defaults = current design (so nothing changes until the admin edits it).
export const DEFAULT_FONTS = {
  heading: { family: "'Cormorant Garamond', serif", weight: 600, spacing: 0 },
  body: { family: "'Outfit', sans-serif", weight: 300, spacing: 0 },
  label: { family: "'Outfit', sans-serif", weight: 600, spacing: 0.02 },
  scale: 1,
};

// Centralised typography — 8 categories, each with sensible defaults. Editable in
// admin; applied site-wide via CSS vars (--fx-ff/fw/fi/ls/lh/sc-<cat>) + .fx-* classes.
export const DEFAULT_TYPO = {
  display: { label: "Grands titres", family: "'Cormorant Garamond', serif", weight: 600, italic: false, spacing: 0, lineHeight: 0.95, scale: 1 },
  section: { label: "Titres de sections", family: "'Cormorant Garamond', serif", weight: 600, italic: false, spacing: 0, lineHeight: 1.05, scale: 1 },
  filmTitle: { label: "Titres de films", family: "'Outfit', sans-serif", weight: 500, italic: false, spacing: 0, lineHeight: 1.25, scale: 1 },
  palmaresTitle: { label: "Titres du Palmarès", family: "'Cormorant Garamond', serif", weight: 600, italic: false, spacing: 0, lineHeight: 1.02, scale: 1 },
  prizeName: { label: "Noms des prix", family: "'Outfit', sans-serif", weight: 600, italic: false, spacing: 0.16, lineHeight: 1.2, scale: 1 },
  body: { label: "Textes courants", family: "'Outfit', sans-serif", weight: 300, italic: false, spacing: 0, lineHeight: 1.6, scale: 1 },
  button: { label: "Boutons", family: "'Outfit', sans-serif", weight: 600, italic: false, spacing: 0.1, lineHeight: 1, scale: 1 },
  nav: { label: "Menu / navigation", family: "'Outfit', sans-serif", weight: 600, italic: false, spacing: 0.18, lineHeight: 1, scale: 1 },
};
export const TYPO_KEYS = ["display", "section", "filmTitle", "palmaresTitle", "prizeName", "body", "button", "nav"];

// Admin-controllable vertical spacing (px) for the main Festival universe sections.
// Safe ranges (0–160) keep the responsive layout intact. Defaults look balanced.
export const DEFAULT_SPACING = { pageTop: 28, pageBottom: 56, films: 24, palmares: 40, jury: 40, editions: 40 };
export const SPACING_MAX = 160;
export const resolveSpacing = (spacing) => {
  const s = { ...DEFAULT_SPACING, ...(spacing || {}) };
  const clamp = (v, d) => { const n = Number(v); return Number.isFinite(n) ? Math.max(0, Math.min(SPACING_MAX, n)) : d; };
  return {
    pageTop: clamp(s.pageTop, DEFAULT_SPACING.pageTop),
    pageBottom: clamp(s.pageBottom, DEFAULT_SPACING.pageBottom),
    films: clamp(s.films, DEFAULT_SPACING.films),
    palmares: clamp(s.palmares, DEFAULT_SPACING.palmares),
    jury: clamp(s.jury, DEFAULT_SPACING.jury),
    editions: clamp(s.editions, DEFAULT_SPACING.editions),
  };
};

// Relative luminance -> pick black/white foreground for best contrast on accent.
export const readableOn = (hex) => {
  if (!hex || hex[0] !== "#") return "#04070d";
  const n = hex.length === 4
    ? hex.slice(1).split("").map((c) => parseInt(c + c, 16))
    : [hex.slice(1, 3), hex.slice(3, 5), hex.slice(5, 7)].map((c) => parseInt(c, 16));
  const [r, g, b] = n.map((v) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; });
  const lum = 0.2126 * r + 0.7152 * g + 0.0715 * b;
  return lum > 0.4 ? "#04070d" : "#ffffff";
};

// Resolve the active theme for the current edition, then expose it as inline CSS vars.
export const resolveTheme = (globalTheme, edition) => {
  const g = { ...DEFAULT_THEME, ...(globalTheme || {}) };
  // Each edition carries its own accent identity. Its theme (if any) is always
  // merged over the global one, so 1 edition = 1 colour, defaulting to the global blue.
  const t = { ...g, ...((edition && edition.theme) || {}) };
  // Global modal window style (border + glow). Follows the edition accent by default.
  const m = { ...DEFAULT_MODAL, ...((globalTheme && globalTheme.modal) || {}) };
  const modalColor = m.colorMode === "custom" && m.color ? m.color : "var(--fx-primary)";
  const modalGlow = Number(m.glow) > 0
    ? `0 0 ${Math.round(m.glow * 55)}px ${Math.round(m.glow * -10)}px color-mix(in srgb, ${modalColor} ${Math.round(45 + m.glow * 25)}%, transparent)`
    : "none";

  // Centralised typography (8 categories). Legacy heading/body/label customisations
  // are carried over so nothing regresses. A global scale multiplies every category.
  const legacy = (globalTheme && globalTheme.fonts) || {};
  const gScale = Number(legacy.scale) || 1;
  const seed = { display: legacy.heading, section: legacy.heading, palmaresTitle: legacy.heading, body: legacy.body, prizeName: legacy.label, button: legacy.label, nav: legacy.label };
  const T = {};
  for (const k of TYPO_KEYS) T[k] = { ...DEFAULT_TYPO[k], ...(seed[k] || {}), ...(legacy[k] || {}) };
  const typoVars = {};
  for (const k of TYPO_KEYS) {
    typoVars[`--fx-ff-${k}`] = T[k].family;
    typoVars[`--fx-fw-${k}`] = String(T[k].weight);
    typoVars[`--fx-fi-${k}`] = T[k].italic ? "italic" : "normal";
    typoVars[`--fx-ls-${k}`] = `${T[k].spacing || 0}em`;
    typoVars[`--fx-lh-${k}`] = String(T[k].lineHeight ?? 1.2);
    typoVars[`--fx-sc-${k}`] = String((Number(T[k].scale) || 1) * gScale);
  }

  return {
    "--fx-primary": t.primary,
    "--fx-on-primary": readableOn(t.primary),
    "--fx-bg": t.bg,
    "--fx-text": t.text,
    "--fx-text2": t.textMuted,
    "--fx-intensity": String(t.intensity ?? 1),
    "--fx-modal-border": modalColor,
    "--fx-modal-border-w": `${m.width != null ? m.width : 1}px`,
    "--fx-modal-glow": modalGlow,
    // Backward-compatible base vars, now driven by the granular categories.
    "--fx-font-heading": T.display.family,
    "--fx-weight-heading": String(T.display.weight),
    "--fx-tracking-heading": `${T.display.spacing || 0}em`,
    "--fx-font-body": T.body.family,
    "--fx-weight-body": String(T.body.weight),
    "--fx-tracking-body": `${T.body.spacing || 0}em`,
    "--fx-font-label": T.button.family,
    "--fx-weight-label": String(T.button.weight),
    ...typoVars,
  };
};
